import React from 'react';
import {
  Box, Paper, TextField, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Divider, CircularProgress, Stack, ToggleButtonGroup,
  ToggleButton, Collapse, IconButton, Typography, Tooltip, Popover, List,
  ListItemButton, ListItemText, MenuItem, LinearProgress, Chip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';

import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

/** ---------- helpers ---------- */
const fmtMoney0 = (n) => (n==null ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 }));
const toCSV = (rows) => rows.map(r => r.map(x => `"${String(x ?? '').replace(/"/g,'""')}"`).join(',')).join('\n');
const downloadCSV = (filename, rows) => {
  try {
    const csv = "\uFEFF" + toCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.style.display='none'; a.href=url; a.setAttribute('download', filename);
    document.body.appendChild(a); a.click(); document.body.removeChild(a); setTimeout(()=>URL.revokeObjectURL(url),0);
  } catch {
    const csv = toCSV(rows);
    const uri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    window.open(uri, '_blank');
  }
};
const sanitize = (s='') => String(s).toLowerCase().replace(/[^a-z0-9\-_.]+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');

/** ---------- date helpers (same style as your page) ---------- */
const NOW = new Date();
const THIS_Y = NOW.getFullYear();
const THIS_M = NOW.getMonth() + 1;
function yearsFrom(start=2024){ const y=THIS_Y; const out=[]; for(let i=start;i<=y;i++) out.push(i); return out; }
function monthsList(){ return Array.from({length:12},(_,i)=>i+1); }
function clampFutureMonth(y, m){ if(y>THIS_Y) return {y:THIS_Y, m:THIS_M}; if(y===THIS_Y && m>THIS_M) return {y,m:THIS_M}; return {y,m}; }
function readableMonth(y,m){ return `${y}-${String(m).padStart(2,'0')}`; }
function isoWeekStr(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7; date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2,'0')}`;
}
function currentIsoWeekParts(){ const s=isoWeekStr(new Date()); const [y,w]=s.split('-W'); return { y:Number(y), w:Number(w) }; }
function weekOfYearList(){ return Array.from({length:53},(_,i)=>i+1); }
function isoWeekStrFromParts(y, w){ return `${y}-W${String(w).padStart(2,'0')}`; }

/** ---------- API roots ---------- */
const API_URL_PVM_SALES = BackofficeReportsApi.getMarginsPvmSalesUrl();
const API_URL_LOCATIONS = Config.API_URL_LOCATIONS + 'list_locations/';

/** ---------- Chip picker (as in your example) ---------- */
function ChipPicker({ label, options, value, onChange }) {
  const [anchor, setAnchor] = React.useState(null);
  const [q, setQ] = React.useState('');
  const open = Boolean(anchor);
  const filtered = (options || []).filter(o => !q || (o.label || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <Chip
        size="small"
        color="default"
        variant="outlined"
        label={
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 0 }}>
            <GridViewIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            <Box
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: { xs: 130, sm: 180 },
              }}
            >
              {label}
            </Box>
          </Box>
        }
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          height: 36,
          borderRadius: '12px',
          color: '#1a237e',
          bgcolor: 'rgba(26, 35, 126, 0.06)',
          borderColor: 'rgba(26, 35, 126, 0.14)',
          fontWeight: 650,
          maxWidth: { xs: '100%', sm: 230 },
          '&:hover': {
            bgcolor: 'rgba(26, 35, 126, 0.10)',
          },
          '& .MuiChip-label': {
            minWidth: 0,
          },
        }}
      />

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setQ('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.75,
            borderRadius: '14px',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.16)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 280 }}>
          <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
          <TextField
            size="small"
            placeholder={`Search ${label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            sx={{
              flex: 1,
              '& .MuiInputBase-root': {
                height: 36,
                borderRadius: '12px',
                fontSize: '0.86rem',
              },
            }}
          />
          <Button
            size="small"
            onClick={() => {
              onChange('__ALL__');
              setAnchor(null);
              setQ('');
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
            }}
          >
            Clear
          </Button>
        </Box>

        <Divider />

        <List dense sx={{ overflowY: 'auto', maxHeight: 300, minWidth: 280, py: 0.5 }}>
          {(filtered || []).map(o => (
            <ListItemButton
              key={o.value}
              dense
              selected={o.value === value}
              onClick={() => {
                onChange(o.value);
                setAnchor(null);
                setQ('');
              }}
              sx={{
                mx: 0.5,
                borderRadius: '10px',
                '&.Mui-selected': {
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                  color: '#1a237e',
                },
              }}
            >
              <ListItemText
                primaryTypographyProps={{
                  noWrap: true,
                  fontSize: '0.86rem',
                  fontWeight: o.value === value ? 750 : 500,
                }}
                primary={o.label}
              />
            </ListItemButton>
          ))}

          {!filtered?.length && (
            <ListItemButton disabled dense>
              <ListItemText primary="No matches" />
            </ListItemButton>
          )}
        </List>
      </Popover>
    </>
  );
}

/** ---------- Page ---------- */
export default function SalesPVMGrossPage(){
  const [client] = React.useState(()=>new Client('none'));

  // filters
  const [open, setOpen] = React.useState(true);
  const [mode, setMode] = React.useState('month'); // month|week
  const [yearMonth, setYearMonth] = React.useState({ y: THIS_Y, m: THIS_M });
  const [yearWeek, setYearWeek] = React.useState(currentIsoWeekParts());
  const [compare, setCompare] = React.useState('prev'); // prev|ly
  const [groupBy, setGroupBy] = React.useState('product'); // product|category
  const [locationOpts, setLocationOpts] = React.useState([{ value:'__ALL__', label:'All Locations' }]);
  const [locationSel, setLocationSel] = React.useState('__ALL__');
  const [deliverySel, setDeliverySel] = React.useState('');
  const [appClientsOnly, setAppClientsOnly] = React.useState(false);

  // data
  const [loading, setLoading] = React.useState(false);
  const [busyOverlay, setBusyOverlay] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [totals, setTotals] = React.useState(null);
  const [meta, setMeta] = React.useState(null);
  const [summary, setSummary] = React.useState('');

  /** summary label (also used in CSV header) */
  const buildSummary = React.useCallback(()=>{
    const pLabel = mode==='month'
      ? `Month ${readableMonth(...Object.values(clampFutureMonth(yearMonth.y, yearMonth.m)))}`
      : `Week ${isoWeekStrFromParts(yearWeek.y, yearWeek.w)}`;
    const locLabel = (locationOpts.find(o=>o.value===locationSel)?.label)||'All Locations';
    const delLabel = (deliverySel===''?'Any':(deliverySel==='1'?'Delivery only':'No delivery'));
    const clientsLabel = appClientsOnly ? 'Clients: App Clients' : 'Clients: All';
    setSummary([pLabel, compare==='prev'?'vs previous':'vs last year', locLabel, delLabel, clientsLabel].join(' · '));
  }, [mode, yearMonth, yearWeek, locationSel, deliverySel, appClientsOnly, compare, groupBy, locationOpts]);

  React.useEffect(()=>{ buildSummary() }, [buildSummary]);

  const period = React.useMemo(()=>{
    if(mode==='month'){
      const c = clampFutureMonth(yearMonth.y, yearMonth.m);
      return readableMonth(c.y, c.m);
    }
    return isoWeekStrFromParts(yearWeek.y, yearWeek.w);
  }, [mode, yearMonth, yearWeek]);

  /** bootstrap options */
  React.useEffect(()=>{
    (async ()=>{
      setLoading(true); setBusyOverlay(true);
      await new Promise(requestAnimationFrame); 
      await new Promise(requestAnimationFrame);
      const shownAt = performance.now();
      try{
        const [lRes] = await Promise.all([
          client.getData(API_URL_LOCATIONS, { status: 1 }) || {},
        ]);
        const locations = (lRes?.locations) || lRes?.results || lRes || [];
        const locOpts = [{ value:'__ALL__', label:'All Locations' },
          ...locations.map(l=>({ value:String(l.iref||l.id), label:l.name })).sort((a,b)=>a.label.localeCompare(b.label))];

        const MIN_VISIBLE = 400;
        const shownFor = performance.now() - shownAt;
        if (shownFor < MIN_VISIBLE) await new Promise(r=>setTimeout(r, MIN_VISIBLE - shownFor));

        setLocationOpts(locOpts);
      } finally {
        setLoading(false); setBusyOverlay(false);
      }
    })();
  }, [client]);

  /** fetch */
  const fetchData = React.useCallback(async ()=>{
    const params = {
      mode, period, compare, groupBy,
      ...(locationSel!=='__ALL__' ? { location: locationSel } : {}),
      ...((deliverySel==='0'||deliverySel==='1') ? { delivery: deliverySel } : {}),
      ...(appClientsOnly ? { appClientsOnly: 1 } : {}),
    };
    setLoading(true); setBusyOverlay(true);
    await new Promise(requestAnimationFrame); 
    await new Promise(requestAnimationFrame);
    const shownAt = performance.now();
    try{
      const data = await client.getData(API_URL_PVM_SALES, params) || {};
      const rws = (data.rows||[]).map((r,i)=>({ id:i, ...r }));
      setRows(rws);
      setTotals(data.totals || null);
      setMeta(data.meta || null);

      const MIN_VISIBLE = 400;
      const shownFor = performance.now() - shownAt;
      if (shownFor < MIN_VISIBLE) await new Promise(r => setTimeout(r, MIN_VISIBLE - shownFor));
    } finally {
      setLoading(false); setBusyOverlay(false);
    }
  }, [client, mode, period, compare, groupBy, locationSel, deliverySel, appClientsOnly]);

  React.useEffect(()=>{ fetchData(); }, []); // initial

  /** CSV with filter summary header */
  const exportCSV = () => {
    // Preface with filters summary
    const pCur = meta?.periods?.current ? `${meta.periods.current.from} to ${meta.periods.current.to}` : '';
    const pBase = meta?.periods?.baseline ? `${meta.periods.baseline.from} to ${meta.periods.baseline.to}` : '';
    const locationLabel = (locationOpts.find(o=>o.value===locationSel)?.label) || 'All Locations';

    const preface = [
      ['Sales P/V (Gross) Export'],
      ['Summary', summary || ''],
      ['Current Window', pCur],
      ['Baseline Window', pBase],
      ['Mode', mode],
      ['Compare', compare],
      ['GroupBy', groupBy],
      ['Location', locationLabel],
      ['Delivery', deliverySel===''?'Any':(deliverySel==='1'?'Yes':'No')],
      ['Clients', appClientsOnly ? 'App Clients' : 'All'],
      [],
    ];

    // Your exact header
    const head = ['Name','Q0','Q1','P0','P1','Price Δ','Vol/Mix Δ','Total Δ','Sales0','Sales1'];

    const list = rows.map(r => {
      const dec = r.decomposition || {};
      const p0 = (r.q0>0) ? (r.sales0 / r.q0) : 0;
      const p1 = (r.q1>0) ? (r.sales1 / r.q1) : 0;
      const totalDelta = (r.sales1||0) - (r.sales0||0);
      return [
        r.groupName || r.name || '',
        r.q0 || 0,
        r.q1 || 0,
        Number.isFinite(p0) ? p0.toFixed(2) : 0,
        Number.isFinite(p1) ? p1.toFixed(2) : 0,
        Math.round(dec.priceImpact || 0),
        Math.round(dec.volMix || 0),
        Math.round(totalDelta),
        Math.round(r.sales0 || 0),
        Math.round(r.sales1 || 0),
      ];
    });

    const fname = [
      'sales_pvm_gross',
      mode,
      String(period).replace(/[^0-9W-]/g,''),
      compare,
      groupBy,
      sanitize(locationLabel || 'all-locations'),
    ].filter(Boolean).join('_') + '.csv';

    downloadCSV(fname, [ ...preface, head, ...list ]);
  };

  const impactSx = (v) => ({
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    color: v > 0 ? 'success.main' : (v < 0 ? 'error.main' : 'text.primary'),
    fontWeight: v !== 0 ? 600 : 700, // keep head bold too
  });

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          xs: 600,
          sm: '100%',
        },
        minHeight: 0,
        maxHeight: {
          xs: 600,
          sm: '100%',
        },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
      }}
    >
      <Paper
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: 0,
          borderRadius: { xs: '14px', sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        {/* Header bar */}
        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.65, sm: 0.85 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 0.75, sm: 1 },
            flexWrap: 'nowrap',
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 0.75 },
              overflow: 'hidden',
            }}
          >
            <Typography
              sx={{
                display: { xs: 'none', sm: 'block' },
                color: '#1a237e',
                fontWeight: 800,
                fontSize: { sm: '0.92rem', md: '1rem' },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexShrink: 0,
              }}
            >
              Sales P/V Gross
            </Typography>

            <Tooltip title="Hide/Show filters">
              <IconButton
                onClick={() => setOpen(o => !o)}
                size="small"
                aria-label="Hide/Show filters"
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: '12px',
                  border: '1px solid rgba(26, 35, 126, 0.12)',
                  color: open ? '#1a237e' : '#64748b',
                  bgcolor: open ? 'rgba(26, 35, 126, 0.08)' : '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(26, 35, 126, 0.12)',
                  },
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Typography
              sx={{
                color: '#64748b',
                fontWeight: 650,
                fontSize: { xs: '0.76rem', sm: '0.82rem' },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {summary}
            </Typography>
          </Box>

          <Tooltip title="Export CSV">
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={exportCSV}
              sx={{
                minWidth: 36,
                width: { xs: 36, sm: 'auto' },
                height: 36,
                px: { xs: 0, sm: 1.1 },
                flexShrink: 0,
                borderRadius: '12px',
                color: '#1a237e',
                bgcolor: 'rgba(26, 35, 126, 0.08)',
                boxShadow: 'none',
                border: '1px solid rgba(26, 35, 126, 0.12)',
                textTransform: 'none',
                fontWeight: 750,
                '&:hover': {
                  bgcolor: 'rgba(26, 35, 126, 0.12)',
                  boxShadow: 'none',
                },
                '& .MuiButton-startIcon': {
                  m: { xs: 0, sm: '0 4px 0 -2px' },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                CSV
              </Box>
            </Button>
          </Tooltip>
        </Box>

        {loading && (
          <LinearProgress
            sx={{
              height: 3,
              flexShrink: 0,
              '& .MuiLinearProgress-bar': {
                transition: 'none',
              },
            }}
          />
        )}

        {/* Filters */}
        <Collapse in={open} timeout="auto" sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              px: { xs: 0.75, sm: 1 },
              py: { xs: 0.75, sm: 1 },
              bgcolor: '#fff',
              borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
              maxHeight: {
                xs: 150,
                sm: 136,
                md: 136,
                lg: 136,
              },
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(15, 23, 42, 0.18)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
              },
            }}
          >
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: { xs: 0.75, sm: 1 },
                pb: 0.25,

                '& .MuiToggleButtonGroup-root': {
                  borderRadius: '12px',
                  overflow: 'hidden',
                  bgcolor: '#fff',
                },
                '& .MuiToggleButton-root': {
                  height: 36,
                  px: { xs: 1, sm: 1.25 },
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                  color: '#64748b',
                  borderColor: 'rgba(15, 23, 42, 0.10)',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  color: '#1a237e',
                  bgcolor: 'rgba(26, 35, 126, 0.08)',
                },
                '& .MuiInputBase-root': {
                  height: 36,
                  borderRadius: '12px',
                  fontSize: { xs: '0.82rem', sm: '0.88rem' },
                  bgcolor: '#fff',
                },
                '& .MuiInputLabel-root': {
                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                },
              }}
            >
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_e, v) => {
                  if (!v) return;
                  setMode(v);
                }}
                size="small"
              >
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="week">Week</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                value={compare}
                exclusive
                onChange={(_e, v) => {
                  if (!v) return;
                  setCompare(v);
                }}
                size="small"
              >
                <ToggleButton value="prev">Previous</ToggleButton>
                <ToggleButton value="ly">Last year</ToggleButton>
              </ToggleButtonGroup>

              <ToggleButtonGroup
                value={groupBy}
                exclusive
                onChange={(_e, v) => {
                  if (!v) return;
                  setGroupBy(v);
                }}
                size="small"
              >
                <ToggleButton value="product">Product</ToggleButton>
                <ToggleButton value="category">Category</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'month' ? (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Year"
                    value={yearMonth.y}
                    onChange={(e) => setYearMonth({ ...yearMonth, y: Number(e.target.value) })}
                    sx={{ width: { xs: 88, sm: 94 } }}
                  >
                    {yearsFrom(2024).map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select
                    size="small"
                    label="Month"
                    value={yearMonth.m}
                    onChange={(e) => setYearMonth({ ...yearMonth, m: Number(e.target.value) })}
                    sx={{ width: { xs: 96, sm: 108 } }}
                  >
                    {monthsList().map(m => (
                      <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <TextField
                    select
                    size="small"
                    label="Year"
                    value={yearWeek.y}
                    onChange={(e) => setYearWeek({ ...yearWeek, y: Number(e.target.value) })}
                    sx={{ width: { xs: 88, sm: 94 } }}
                  >
                    {yearsFrom(2024).map(y => (
                      <MenuItem key={y} value={y}>{y}</MenuItem>
                    ))}
                  </TextField>

                  <Tooltip title="See Current/Baseline dates below after load">
                    <TextField
                      select
                      size="small"
                      label="ISO Week"
                      value={yearWeek.w}
                      onChange={(e) => setYearWeek({ ...yearWeek, w: Number(e.target.value) })}
                      sx={{ width: { xs: 104, sm: 120 } }}
                    >
                      {weekOfYearList().map(w => (
                        <MenuItem key={w} value={w}>W{String(w).padStart(2, '0')}</MenuItem>
                      ))}
                    </TextField>
                  </Tooltip>
                </Stack>
              )}

              <ChipPicker
                label={(locationOpts.find(o => o.value === locationSel)?.label) || 'All Locations'}
                options={locationOpts}
                value={locationSel}
                onChange={setLocationSel}
              />

              <TextField
                select
                size="small"
                label="Delivery"
                value={deliverySel}
                onChange={(e) => setDeliverySel(e.target.value)}
                sx={{ width: { xs: 108, sm: 116 } }}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="0">No</MenuItem>
                <MenuItem value="1">Yes</MenuItem>
              </TextField>

              <TextField
                select
                size="small"
                label="Clients"
                value={appClientsOnly ? '1' : ''}
                onChange={(e) => setAppClientsOnly(e.target.value === '1')}
                sx={{ width: { xs: 104, sm: 112 } }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">App Clients</MenuItem>
              </TextField>

              <Button
                size="small"
                variant="contained"
                startIcon={<SearchIcon fontSize="small" />}
                onClick={() => {
                  buildSummary();
                  fetchData();
                }}
                sx={{
                  height: 36,
                  borderRadius: '12px',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 750,
                  px: 1.25,
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Collapse>

        {/* Totals summary */}
        <Box
          sx={{
            px: { xs: 0.75, sm: 1 },
            py: { xs: 0.65, sm: 1 },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 0.75, sm: 1 },
              gridTemplateColumns: {
                xs: 'repeat(3, minmax(0, 1fr))',
                sm: 'repeat(5, minmax(0, 1fr))',
              },
            }}
          >
            {[
              {
                label: 'Δ Sales',
                value: fmtMoney0((totals?.sales1 || 0) - (totals?.sales0 || 0)),
                accent: true,
              },
              {
                label: 'Price',
                value: fmtMoney0(totals?.decomposition?.priceImpact || 0),
              },
              {
                label: 'Vol/Mix',
                value: fmtMoney0(totals?.decomposition?.volMix || 0),
              },
              {
                label: 'Sales0',
                value: fmtMoney0(totals?.sales0 || 0),
              },
              {
                label: 'Sales1',
                value: fmtMoney0(totals?.sales1 || 0),
              },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: { xs: 0.75, sm: 1.15 },
                  borderRadius: '14px',
                  minWidth: 0,
                  border: item.accent
                    ? '1px solid rgba(26, 35, 126, 0.16)'
                    : '1px solid rgba(15, 23, 42, 0.06)',
                  bgcolor: item.accent
                    ? 'rgba(26, 35, 126, 0.06)'
                    : 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
                }}
              >
                <Typography
                  sx={{
                    color: item.accent ? '#1a237e' : '#64748b',
                    fontWeight: 700,
                    fontSize: { xs: '0.68rem', sm: '0.78rem' },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#111827',
                    fontWeight: 800,
                    fontSize: { xs: '0.92rem', sm: '1.14rem', md: '1.22rem' },
                    lineHeight: 1.05,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Table area */}
       <Box
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
          {busyOverlay && (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(15, 23, 42, 0.16)',
                backgroundImage:
                  'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20,
                pointerEvents: 'auto',
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: '14px',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                <CircularProgress size={22} color="inherit" />
                <Typography variant="body2">Preparing report…</Typography>
              </Paper>
            </Box>
          )}

          <TableContainer
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              '&::-webkit-scrollbar': {
                height: 8,
                width: 8,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(15, 23, 42, 0.18)',
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(15, 23, 42, 0.04)',
              },
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                minWidth: {
                  xs: 680,
                  sm: 760,
                  md: '100%',
                },
                width: '100%',
                tableLayout: {
                  xs: 'fixed',
                  md: 'auto'
                },
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
                  fontSize: {
                    xs: '0.82rem',
                    sm: '0.88rem',
                    md: '0.92rem',
                  },
                },
                '& .MuiTableRow-hover:hover': {
                  backgroundColor: 'rgba(26, 35, 126, 0.035)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      py: { xs: 0.65, sm: 0.85 },
                      px: { xs: 0.75, sm: 1 },
                      bgcolor: '#f8fafc',
                      color: '#1a237e',
                      fontWeight: 650,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      left: 0,
                      zIndex: 4,
                      width: { xs: 190, sm: 240, md: '34%' },
                    }}
                  >
                    Name
                  </TableCell>

                  {['Δ Sales', 'Sales0', 'Vol mix', 'Price', 'Sales1'].map((h) => (
                    <TableCell
                      key={h}
                      align="right"
                      sx={{
                        py: { xs: 0.65, sm: 0.85 },
                        px: { xs: 0.75, sm: 1 },
                        bgcolor: '#f8fafc',
                        color: '#1a237e',
                        fontWeight: 650,
                        whiteSpace: 'nowrap',
                        width: { xs: 98, sm: 112, md: '13.2%' },
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map(r => {
                  const vol = r?.decomposition?.volMix ?? 0;
                  const pri = r?.decomposition?.priceImpact ?? 0;
                  const title = r.groupName || r.name || '';

                  return (
                    <TableRow key={r.id} hover title={title}>
                      <TableCell
                        sx={{
                          py: { xs: 0.65, sm: 0.8 },
                          px: { xs: 0.75, sm: 1 },
                          position: 'sticky',
                          left: 0,
                          zIndex: 2,
                          bgcolor: '#fff',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontWeight: 550,
                        }}
                      >
                        {title}
                      </TableCell>

                      <TableCell align="right" sx={{ ...impactSx(r.sales1 - r.sales0), py: { xs: 0.65, sm: 0.8 }, px: { xs: 0.75, sm: 1 }, fontWeight: 500 }}>
                        {fmtMoney0(r.sales1 - r.sales0)}
                      </TableCell>

                      <TableCell align="right" sx={{ py: { xs: 0.65, sm: 0.8 }, px: { xs: 0.75, sm: 1 }, fontVariantNumeric: 'tabular-nums', fontWeight: 450 }}>
                        {fmtMoney0(r.sales0)}
                      </TableCell>

                      <TableCell align="right" sx={{ ...impactSx(vol), py: { xs: 0.65, sm: 0.8 }, px: { xs: 0.75, sm: 1 }, fontWeight: 500 }}>
                        {fmtMoney0(vol)}
                      </TableCell>

                      <TableCell align="right" sx={{ ...impactSx(pri), py: { xs: 0.65, sm: 0.8 }, px: { xs: 0.75, sm: 1 }, fontWeight: 500 }}>
                        {fmtMoney0(pri)}
                      </TableCell>

                      <TableCell align="right" sx={{ py: { xs: 0.65, sm: 0.8 }, px: { xs: 0.75, sm: 1 }, fontVariantNumeric: 'tabular-nums', fontWeight: 450 }}>
                        {fmtMoney0(r.sales1)}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!rows.length && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box
                        sx={{
                          py: 4,
                          color: 'text.secondary',
                          fontSize: { xs: '0.86rem', sm: '0.92rem' },
                        }}
                      >
                        No data
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Period info */}
        {meta?.periods && (
          <Box
            sx={{
              px: { xs: 0.75, sm: 1 },
              py: 0.75,
              borderTop: '1px solid rgba(15, 23, 42, 0.06)',
              bgcolor: '#fff',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: { xs: '0.72rem', sm: '0.78rem' },
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              Current: {meta.periods.current.from} to {meta.periods.current.to} · Baseline: {meta.periods.baseline.from} to {meta.periods.baseline.to}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
